import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from '../patient.model';

interface GastrointestinalFields {
  // Bowel sounds

  /**
   * The bowel sounds occurring in the patient's right upper quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsRuq: string;

  /**
   * The bowel sounds occurring in the patient's right lower quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsRlq: string;

  /**
   * The bowel sounds occurring in the patient's left upper quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsLuq: string;

  /**
   * The bowel sounds occurring in the patient's left lower quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsLlq: string;

  /**
   * Description of abnormal bowel movement findings.
   */
  abnormalBowelMovement: string;

  /**
   * Whether the patient is nauseous (true) or not (false).
   */
  nausea: boolean;

  /**
   * The amount of the patient's emesis (vomit).
   *
   * Options: small, medium, or large.
   */
  emesis: string;

  /**
   * Whether the patient's bowel control is incontinent (true) or not (false).
   */
  incontinence: boolean;

  /**
   * Whether hemorrhoids is present (true) or absent (false).
   */
  hemorrhoids: boolean;

  // Ostomy

  /**
   * The color of the patient's stoma.
   *
   * Options: pink, beefy red, pale, or purple.
   */
  ostomyStomaSite: string;

  /**
   * Describes the patient's output.
   *
   * Options: loose, watery, or formed.
   */
  ostomyOutput: string;

  oralDiet: string;
  tubeFeed: boolean;
  tubeType: string;
  bagChangedHours: string;
  siteAssessment: string;
  reducedAppetite: boolean;
  weightLossPercentage: string;

  nutritionScore: string;

  /* pediatric olnly */
  abnormalUmbilicalCord: boolean;
  umbilicalCordDetails: string;
  otherFindings: string;
}
export type GastrointestinalFormGroup = FormGroup<ControlsOf<GastrointestinalFields>>;

export const GastrointestinalAssessmentHelpers: AssessmentHelpers<GastrointestinalAssessment, Patient> = {
  createFormGroup: createGastrointestinalFormGroup,
  toPDF: gastrointestinalAssessmentToPDF,
  formToObject: formToGastrointestinalAssessment,
  initialState: {
    bowelSoundsRuq: '', // hypoactive, hyperactive, absent
    bowelSoundsRlq: '',
    bowelSoundsLuq: '',
    bowelSoundsLlq: '',
    abnormalBowelMovement: '',
    nausea: false,
    emesis: '', // small, medium, large
    incontinence: false,
    hemorrhoids: false, // present (true), absent (false)
    ostomyStomaSite: '', // pink, beefy red, pale, purple
    ostomyOutput: '', // loose, watery, formed
    oralDiet: '',
    tubeFeed: false,
    tubeType: '',
    bagChangedHours: '',
    siteAssessment: '',
    reducedAppetite: false,
    weightLossPercentage: '',
    nutritionScore: '',
    abnormalUmbilicalCord: false,
    umbilicalCordDetails: '',
    otherFindings: '',
  },
};


/**
 * Holds information about the patient's abdominal/gastrointestinal assessment.
 */
export class GastrointestinalAssessment {
  bowelSoundsRuq: string;
  bowelSoundsRlq: string;
  bowelSoundsLuq: string;
  bowelSoundsLlq: string;
  abnormalBowelMovement: string;
  nausea: boolean;
  emesis: string;
  incontinence: boolean;
  hemorrhoids: boolean;
  ostomyStomaSite: string;
  ostomyOutput: string;
  oralDiet: string;
  tubeFeed: boolean;
  tubeType: string;
  bagChangedHours: string;
  siteAssessment: string;
  reducedAppetite: boolean;
  weightLossPercentage: string;
  nutritionScore: string;
  abnormalUmbilicalCord: boolean;
  umbilicalCordDetails: string;
  otherFindings: string;

  constructor(data: Partial<GastrointestinalFields>) {
    const is = GastrointestinalAssessmentHelpers.initialState;
    this.bowelSoundsRuq = data.bowelSoundsRuq || is['bowelSoundsRuq'];
    this.bowelSoundsRlq = data.bowelSoundsRlq || is['bowelSoundsRlq'];
    this.bowelSoundsLuq = data.bowelSoundsLuq || is['bowelSoundsLuq'];
    this.bowelSoundsLlq = data.bowelSoundsLlq || is['bowelSoundsLlq'];
    this.abnormalBowelMovement = data.abnormalBowelMovement || is['abnormalBowelMovement'];
    this.nausea = data.nausea || is['nausea'];
    this.emesis = data.emesis || is['emesis'];
    this.incontinence = data.incontinence || is['incontinence'];
    this.hemorrhoids = data.hemorrhoids || is['hemorrhoids'];
    this.ostomyStomaSite = data.ostomyStomaSite || is['ostomyStomaSite'];
    this.ostomyOutput = data.ostomyOutput || is['ostomyOutput'];
    this.oralDiet = data.oralDiet || is['oralDiet'];
    this.tubeFeed = data.tubeFeed || is['tubeFeed'];
    this.tubeType = data.tubeType || is['tubeType'];
    this.bagChangedHours = data.bagChangedHours || is['bagChangedHours'];
    this.siteAssessment = data.siteAssessment || is['siteAssessment'];
    this.reducedAppetite = data.reducedAppetite || is['reducedAppetite'];
    this.weightLossPercentage = data.weightLossPercentage || is['weightLossPercentage'];
    this.nutritionScore = data.nutritionScore || is['nutritionScore'];
    this.abnormalUmbilicalCord = data.abnormalUmbilicalCord ?? is['abnormalUmbilicalCord'];
    this.umbilicalCordDetails = data.umbilicalCordDetails || is['umbilicalCordDetails'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}

function createGastrointestinalFormGroup(gastrointestinalAssessment?: GastrointestinalAssessment) {
  const data = gastrointestinalAssessment || new GastrointestinalAssessment(GastrointestinalAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<GastrointestinalFields>>({
    bowelSoundsRuq: new FormControl<string | null>(data.bowelSoundsRuq),
    bowelSoundsRlq: new FormControl<string | null>(data.bowelSoundsRlq),
    bowelSoundsLuq: new FormControl<string | null>(data.bowelSoundsLuq),
    bowelSoundsLlq: new FormControl<string | null>(data.bowelSoundsLlq),
    abnormalBowelMovement: new FormControl<string | null>(data.abnormalBowelMovement),
    nausea: new FormControl<boolean | null>(data.nausea),
    emesis: new FormControl<string | null>(data.emesis),
    incontinence: new FormControl<boolean | null>(data.incontinence),
    hemorrhoids: new FormControl<boolean | null>(data.hemorrhoids),
    ostomyStomaSite: new FormControl<string | null>(data.ostomyStomaSite),
    ostomyOutput: new FormControl<string | null>(data.ostomyOutput),
    oralDiet: new FormControl<string | null>(data.oralDiet),
    tubeFeed: new FormControl<boolean | null>(data.tubeFeed),
    tubeType: new FormControl<string | null>(data.tubeType),
    bagChangedHours: new FormControl<string | null>(data.bagChangedHours),
    siteAssessment: new FormControl<string | null>(data.siteAssessment),
    reducedAppetite: new FormControl<boolean | null>(data.reducedAppetite),
    weightLossPercentage: new FormControl<string | null>(data.weightLossPercentage),
    nutritionScore: new FormControl<string | null>(data.nutritionScore),
    abnormalUmbilicalCord: new FormControl<boolean | null>(data.abnormalUmbilicalCord),
    umbilicalCordDetails: new FormControl<string | null>(data.umbilicalCordDetails),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function gastrointestinalAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: GastrointestinalAssessment, patient: Patient): void {
  pdf.addSectionTitle("Abdominal");
  pdf.pdf.text("Bowel Sounds", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("LUQ", assessment.bowelSoundsLuq, "RUQ", assessment.bowelSoundsRuq);
  pdf.addTwoColumnText("LLQ", assessment.bowelSoundsLlq, "RLQ", assessment.bowelSoundsRlq);
  pdf.addTwoColumnText("Abnormal Bowel Movement", assessment.abnormalBowelMovement,
    "Nausea", assessment.nausea ? "Yes" : "No");
  pdf.addTwoColumnText("Emesis", assessment.emesis, "Bowel Control", assessment.incontinence ? "Yes" : "No");
  pdf.currY += 10;
  pdf.pdf.text("Ostomy", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Stoma Site", assessment.ostomyStomaSite, "Output", assessment.ostomyOutput);
  pdf.currY += 10;
  pdf.pdf.text("Hemorrhoids", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Hemorrhoids Present", assessment.hemorrhoids ? "Yes" : "No", "", "");
  pdf.currY += 10;
  pdf.pdf.text("Nutrition", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Reduced Appetite", assessment.reducedAppetite ? "Yes" : "No", "Weight Loss %", assessment.weightLossPercentage);
  pdf.addTwoColumnText("Oral Diet", assessment.oralDiet, "Tube Feed", assessment.tubeFeed ? "Yes" : "No");
  pdf.addOneColumnText("Tube Type", assessment.tubeType);
  pdf.addOneColumnText("Hours Since Bag Change", assessment.bagChangedHours);
  pdf.addOneColumnText("Site Assessment", assessment.siteAssessment);
  pdf.addOneColumnText("Nutrition Score", assessment.nutritionScore);
  pdf.currY += 20;
  if (patient.isPediatric()) {
    pdf.pdf.text("Other", pdf.margin, pdf.currY);
    pdf.currY += 20;
    pdf.addOneColumnText("Abnormal Umbilical Cord", assessment.abnormalUmbilicalCord ? "Yes" : "No");
    if (assessment.abnormalUmbilicalCord) {
      pdf.addOneColumnText("Umbilical Cord Details", assessment.umbilicalCordDetails);
    }
  }
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
  pdf.currY += 20;
}

function formToGastrointestinalAssessment(fg: GastrointestinalFormGroup) {
  return new GastrointestinalAssessment({
    bowelSoundsRuq: fg.controls.bowelSoundsRuq.value!,
    bowelSoundsRlq: fg.controls.bowelSoundsRlq.value!,
    bowelSoundsLuq: fg.controls.bowelSoundsLuq.value!,
    bowelSoundsLlq: fg.controls.bowelSoundsLlq.value!,
    abnormalBowelMovement: fg.controls.abnormalBowelMovement.value!,
    nausea: fg.controls.nausea.value!,
    emesis: fg.controls.emesis.value!,
    incontinence: fg.controls.incontinence.value!,
    hemorrhoids: fg.controls.hemorrhoids.value!,
    ostomyStomaSite: fg.controls.ostomyStomaSite.value!,
    ostomyOutput: fg.controls.ostomyOutput.value!,
    oralDiet: fg.controls.oralDiet.value!,
    tubeFeed: fg.controls.tubeFeed.value!,
    tubeType: fg.controls.tubeType.value!,
    bagChangedHours: fg.controls.bagChangedHours.value!,
    siteAssessment: fg.controls.siteAssessment.value!,
    reducedAppetite: fg.controls.reducedAppetite.value!,
    weightLossPercentage: fg.controls.weightLossPercentage.value!,
    nutritionScore: fg.controls.nutritionScore.value!,
    abnormalUmbilicalCord: fg.controls.abnormalUmbilicalCord.value!,
    umbilicalCordDetails: fg.controls.umbilicalCordDetails.value!,
    otherFindings: fg.controls.otherFindings.value!,
  });
}